import { useState } from "react";
import { Lock, Mail, Check, AlertCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useCreerMessageContact } from "@/hooks/useMessageContact";
import { useSeo } from "@/hooks/useSeo";
import { truncate } from "@/lib/seo";

function ContactPage() {
  const { t } = useI18n();
  useSeo({
    title: `${t("nav.contact")} — Ramba News`,
    description: truncate(t("contact.description")),
    path: "/contact",
  });

  const creerMessage = useCreerMessageContact();
  const [email, setEmail] = useState("");
  const [nom, setNom] = useState("");
  const [message, setMessage] = useState("");
  const [confidentiel, setConfidentiel] = useState(false);
  const [envoye, setEnvoye] = useState(false);
  const [erreur, setErreur] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErreur("");
    creerMessage.mutate(
      { email: email.trim(), nom: nom.trim() || undefined, message: message.trim(), confidentiel },
      {
        onSuccess: () => {
          setEnvoye(true);
          setEmail("");
          setNom("");
          setMessage("");
          setConfidentiel(false);
        },
        onError: (e: any) => setErreur(e?.response?.data?.message || "Échec de l'envoi, réessayez plus tard."),
      }
    );
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10 md:py-14">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-verified">{t("nav.contact")}</div>
      <h1 className="mt-2 font-serif text-3xl md:text-5xl font-semibold tracking-tight">{t("contact.heading")}</h1>
      <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
        {t("contact.description")}
      </p>

      <div className="mt-10 rounded-md border border-border bg-card p-6 md:p-8">
        {envoye ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Check className="h-10 w-10 text-verified" />
            <p className="text-sm text-foreground">Votre message a bien été envoyé. Merci de nous avoir contactés.</p>
            <button
              onClick={() => setEnvoye(false)}
              className="mt-2 text-xs font-semibold text-verified hover:underline"
            >
              Envoyer un autre message
            </button>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
            {erreur && (
              <div className="flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-3 py-2 text-xs text-alert">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {erreur}
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("form.email")}
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 block w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-verified"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("form.name")}
              </label>
              <input
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="mt-1.5 block w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-verified"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("form.message")}
              </label>
              <textarea
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1.5 block w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-verified"
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                id="confidential"
                checked={confidentiel}
                onChange={(e) => setConfidentiel(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              <label htmlFor="confidential">{t("form.confidentialTip")}</label>
            </div>
            <button
              type="submit"
              disabled={creerMessage.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-foreground px-5 py-2.5 text-sm font-semibold text-background hover:opacity-90 disabled:opacity-60"
            >
              <Mail className="h-4 w-4" /> {creerMessage.isPending ? "Envoi…" : t("form.submit")}
            </button>
          </form>
        )}
      </div>

      <div className="mt-6 rounded-md border border-verified/30 bg-verified/5 p-4 text-sm">
        <div className="flex items-start gap-2">
          <Lock className="h-4 w-4 mt-0.5 text-verified shrink-0" />
          <p className="text-foreground/90">
            {t("contact.security")}
          </p>
        </div>
      </div>

      {/* <div className="mt-6 grid gap-3 text-sm md:grid-cols-2">
        <div className="rounded-md border border-border bg-surface p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("contact.general")}</div>
          <div className="mt-1 font-mono text-foreground">rambanews@protonmail.com</div>
        </div> */}
        {/* <div className="rounded-md border border-border bg-surface p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("contact.tips")}</div>
          <div className="mt-1 font-mono text-foreground">tips@ramba-news.example</div>
        </div> */}
      {/* </div> */}
    </div>
  );
}

export default ContactPage;
